import DataGrid, { DataGridConfig } from 'components/DataGrid';
import PageLayout from 'components/PageLayout';
import { useAtomValue } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, {
  Fragment,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CircleSpinner } from 'react-spinners-kit';
import { Alert, Button } from 'reactstrap';
import { useAccountsResetAtoms } from 'views/sast2/Accounts/state';
import { BASE_URL, HostType } from 'views/sast2/helper';
import { useRepositoryResetAtoms } from 'views/sast2/Repositories/state';
import { companyIdAtom } from 'views/state';

import BranchSelector from './BranchSelector';
import Filters from './Filters';
import history from '../../../../history.js';
import AccountInfoSummary from '../AccountInfoSummary';

// Assuming you have a function to get the authorization token if needed
// import { getAuthToken } from '../path/to/auth';

const AddRepositories = (_props) => {
  const companyId = useAtomValue(companyIdAtom);
  const [showArchiveRepositories, setShowArchiveRepositories] =
    useState<boolean>(false);
  const [accountId, setAccountId] = useState<number>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [accountInfo, setAccountInfo] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [repositories, setRepositories] = useState<any[]>();
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [isBranchesSelected, setIsBranchesSelected] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const resetRepositoryAtoms = useRepositoryResetAtoms();
  const resetAccountsAtoms = useAccountsResetAtoms();
  const hasMounted = useRef(false);

  useEffect(
    () => {
      const searchParams = new URLSearchParams(window.location.search);
      const id = searchParams.get('account');
      if (id) setAccountId(parseInt(id));
    },
    [
      /*on mount*/
    ],
  );

  useEffect(() => {
    const fetchData = async () => {
      const urlParams = {
        company_id: companyId,
        account_id: accountId,
        show_archived: showArchiveRepositories,
      };

      setLoading(true);

      callApi('sast/accounts/fetch-repositories/', 'GET', urlParams)
        .then((response) => {
          if (response.status === 201 || response.status === 200) {
            setRepositories(response?.data?.repos);
            setAccountInfo(response?.data?.account);
          }
        })
        .catch((err) => {
          showFailureToast(
            `Failed to load repositories. ${err?.statusText}: ${err?.data?.error}`,
          );
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (accountId) {
      fetchData();
    }
  }, [accountId, showArchiveRepositories]);

  useEffect(() => {
    if (hasMounted.current) {
      // Redirect to the dashboard if companyId changes after the initial mount
      history.push('/dashboard#sast2');
    } else {
      hasMounted.current = true;
    }
  }, [companyId]);

  useEffect(() => {
    // validate that for all selected repositories, the branch is selected or not
    handleBranchesSelectionUpdated();
    handleRepositorySelectionUpdated();
  }, [repositories, selectedRowIds]);

  const getSelectedRepositories = () => {
    return repositories?.filter((x) => selectedRowIds.includes(x.id));
  };

  const handleBranchesSelectionUpdated = () => {
    let branchesSelectedForAll = true;
    if (repositories) {
      const selectedRepositories = getSelectedRepositories();
      if (selectedRepositories) {
        for (const repository of selectedRepositories) {
          if (!repository['new_branches']) {
            branchesSelectedForAll = false;
            break;
          }
        }
      }
    }
    setIsBranchesSelected(branchesSelectedForAll);
  };

  const handleRepositorySelectionUpdated = () => {
    if (repositories) {
      const nonSelectedRepositoriesWithSelectedBranches = repositories?.filter(
        (repo) => !selectedRowIds.includes(repo.id) && repo['new_branches'],
      );
      if (nonSelectedRepositoriesWithSelectedBranches.length > 0) {
        nonSelectedRepositoriesWithSelectedBranches.forEach((repo) => {
          if (repo['new_branches']) {
            repo['new_branches'] = null;
          }
        });
      }
    }
  };

  const handleAddRepositories = async () => {
    setLoading(true);
    // API call to save selected repositories
    if (isBranchesSelected) {
      const selectedRepositories = getSelectedRepositories();
      const reposData = selectedRepositories?.map((repo) => {
        return {
          id: repo.id,
          name: repo.name,
          full_name: repo.full_name,
          url: repo.url,
          branches_url: repo.branches_url,
          description: repo.description,
          pushed_at: repo.pushed_at,
          new_branches: repo.new_branches,
          json_info: repo.json_info,
          repository: repo.repository,
          workspace: repo.workspace,
          workspace_slug: repo.workspace_slug,
          organization: repo.organization,
          project: repo.project,
        };
      });

      // {
      //     "id": 378612154,
      //     "name": "house",
      //     "full_name": "abdulhannan709/house",
      //     "url": "https://github.com/abdulhannan709/house",
      //     "description": "None",
      //     "created_at": "2021-06-20T10:00:14Z",
      //     "pushed_at": "2023-05-22T14:35:55Z",
      //     "branches_url": "https://api.github.com/repos/abdulhannan709/house/branches{/branch}"
      // }

      const data = {
        account_id: accountId,
        company_id: companyId,
        repositories: reposData,
      };

      // console.log("requestData:", data);
      callApi(`sast/repositories/add_repositories/`, 'POST', {}, data)
        .then((res) => {
          if (res.status === 201 || res.status === 200) {
            showSuccessToast(
              'Selected repositories/branches successfully added.',
            );
            // invalidate the repos state to force reload the repository listing
            resetRepositoryAtoms();
            resetAccountsAtoms();
            history.push(`/${BASE_URL}/dashboard?t=repo#sast2`);
          }
        })
        .catch((err) => {
          showFailureToast(
            `Failed to add selected repositories. ${err?.statusText}: ${err?.data?.error}`,
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const isRowSelected = (rowData): boolean => {
    return selectedRowIds?.includes(rowData['id']) || false;
  };

  function getApplicableColumnKeys(): string[] {
    switch (accountInfo?.host_type) {
      // NOTE: added github_ prefix to avoid conflict with azure organization
      case HostType.Github:
        return [
          'id',
          'github_organization',
          'name',
          'branch',
          'connected_branches',
          'archived',
        ];
      case HostType.Gitlab:
        return [
          'id',
          'group_name',
          'name',
          'branch',
          'connected_branches',
          'archived',
        ];
      case HostType.Azure:
        return ['id', 'organization', 'name', 'branch', 'connected_branches'];
      case HostType.Bitbucket:
        return ['id', 'workspace', 'name', 'branch', 'connected_branches'];
      default:
        return ['id', 'name', 'branch', 'connected_branches'];
    }
  }

  /**
     * {
      "id": 378612154,
      "name": "house",
      "full_name": "abdulhannan709/house",
      "html_url": "https://github.com/abdulhannan709/house",
      "description": null,
      "created_at": "2021-06-20T10:00:14Z",
      "pushed_at": "2023-05-22T14:35:55Z"
  }
     * @returns 
     */
  const getAccountConfig = (): DataGridConfig => ({
    rowIdKey: 'id',
    columns: [
      { key: 'id', label: 'Id' },
      // AZURE ORGANIZATION
      {
        key: 'organization',
        label: 'Organization/Project',
        render: (rowData) => (
          <Fragment>
            <strong>{rowData.organization}</strong>/{rowData.project}
          </Fragment>
        ),
      },
      // Github ORGANIZATION
      {
        // NOTE: added github_ prefix to avoid conflict with azure organization
        key: 'github_organization',
        label: 'Organization',
        render: (rowData) => <Fragment>{rowData.organization}</Fragment>,
      },
      // Bitbucket workspace
      {
        key: 'workspace',
        label: 'Workspace',
      },
      // GITLAB GROUP
      {
        key: 'group_name',
        label: 'Group',
        // NOTE: auto width for one column to expand automatically
        headerCellStyle: { width: 'auto' },
        rowCellStyle: { width: 'auto' },
      },
      {
        key: 'workspaces',
        label: 'Workspaces',
      },
      {
        key: 'name',
        label: 'Repository Name',
        tooltip: (rowData) => (
          <>
            {' '}
            <strong>Full name:</strong> {rowData['full_name']}
          </>
        ),
      },
      {
        key: 'branch',
        label: 'Available Branches',
        // NOTE: auto width for one column to expand automatically

        overrideContents: (rowData) => (
          <BranchSelector
            accountId={accountId}
            // undefined value for no selection, do not load branches yet
            onBranchSelection={handleBranchesSelectionUpdated}
            repoData={isRowSelected(rowData) ? rowData : undefined}
          />
        ),
        tooltip: (_rowData) =>
          'Select row checkbox to enable branch selection.',
        headerTooltip:
          'Select the new branches to be connected. Select a row checkbox to enable branch selection.',
        headerCellStyle: { width: '25%' },
        rowCellStyle: { width: '25%' },
      },
      {
        key: 'connected_branches',
        label: 'Connected Branches',
        headerTooltip:
          'The branches that are already connected for this repository.',
        overrideContents: (rowData) => {
          const branch_badges = rowData['connected_branches']?.map((branch) => (
            <span key={branch} className="badge badge-secondary border">
              {branch}
            </span>
          ));

          return <>{branch_badges}</>;
        },
        headerCellStyle: { width: '25%' },
        rowCellStyle: { width: '25%' },
      },
      {
        key: 'archived',
        label: 'Archived',
      },
    ],
    rowSelectionConfig: {
      preSelectedRows: selectedRowIds,
      onSelectionChange: (updatedRows) => setSelectedRowIds(updatedRows),
    },
    columnsDisplayConfig: {
      enabledColumnKeys: getApplicableColumnKeys(),
    },
  });

  const config = useMemo<DataGridConfig>(
    () => getAccountConfig(),
    [accountInfo, repositories, selectedRowIds],
  );

  return (
    <PageLayout
      id="integrations"
      // heading="Add repositories for scanning"
      heading="Connect Repositories/Branches"
      description="Select and connect the repositories/branches for this account for vulnerability scanning:"
      filteringOptions={
        <Suspense fallback={<>...</>}>
          {['github', 'gitlab'].includes(accountInfo?.host_type) && (
            <Filters
              showArchiveRepositories={showArchiveRepositories}
              setShowArchiveRepositories={setShowArchiveRepositories}
            />
          )}
        </Suspense>
      }
      footerContents={
        <div className="container-fluid text-right">
          {!isBranchesSelected && (
            <Alert color="primary" className="text-left">
              Select at least one branch for all selected repositories to
              proceed.{' '}
            </Alert>
          )}
          {loading ? (
            <div>
              <CircleSpinner size={20} color="#0665a9" loading={true} />
            </div>
          ) : (
            <Button
              onClick={() =>
                history.push(`/${BASE_URL}/dashboard?t=account#sast2`)
              }
              color="secondary"
            >
              Back to Account Listing
            </Button>
          )}
          <Button
            disabled={
              selectedRowIds?.length === 0 || !isBranchesSelected || loading
            }
            onClick={() => handleAddRepositories()}
            color="primary"
          >
            <i className="fa fa-plus-square fa-lg"></i>&nbsp;&nbsp;&nbsp; Add
            Selected Repositories/Branches
          </Button>
        </div>
      }
    >
      {accountInfo ? (
        <AccountInfoSummary
          props={accountInfo}
          description={undefined}
          mode={'normal'}
        />
      ) : (
        <></>
      )}
      <DataGrid config={config} data={repositories} loading={loading} />
    </PageLayout>
  );
};

export default AddRepositories;
